/**
 * Application shell.
 *
 * Layout: a toolbar across the top, a settings column on the left, the viewport
 * in the middle and a live Info column on the right. The columns are fixed width
 * and independently scrollable so the viewport keeps a stable size while the
 * panels are used.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Viewport } from './Viewport.tsx';
import { applyRequestedBackend, getRuntime, useRuntime } from './runtime.ts';
import { Toolbar } from '../ui/Toolbar.tsx';
import { SettingsPanel } from '../ui/SettingsPanel.tsx';
import { InfoPanel } from '../ui/InfoPanel.tsx';
import {
  decodeWorld,
  download,
  encodeWorld,
  heightmapToPngBlob,
  heightmapToState,
  pickFile,
  pngBlobToHeightmap,
  stateToHeightmap,
} from './fileIO.ts';
import { VIEW_MODES } from '../rendering/viewModes.ts';
import { Activity, SlidersHorizontal, X } from 'lucide-react';

const desktopPanelsByDefault = (): boolean =>
  typeof window === 'undefined' || window.matchMedia('(min-width: 768px)').matches;

export function App() {
  const runtime = useRuntime();
  // Exposed for the headless smoke test, which drives the real UI rather than a
  // reimplementation of it.
  (window as unknown as { __runtime?: unknown }).__runtime = runtime;
  const [brushActive, setBrushActive] = useState(false);
  const [fps, setFps] = useState(60);
  const [renderMs, setRenderMs] = useState(0);
  const [simMs, setSimMs] = useState(0);
  const [rasterWidth, setRasterWidth] = useState(runtime.renderWidth);
  const [notice, setNotice] = useState<string | null>(null);
  const [toolbarOpen, setToolbarOpen] = useState(desktopPanelsByDefault);
  const [settingsOpen, setSettingsOpen] = useState(desktopPanelsByDefault);
  const [infoOpen, setInfoOpen] = useState(desktopPanelsByDefault);
  const noticeTimer = useRef<number>(0);

  useEffect(() => {
    void applyRequestedBackend(runtime);
  }, [runtime]);

  const flash = useCallback((message: string) => {
    setNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const onFrameStats = useCallback((f: number, r: number, s: number, raster: number) => {
    setFps(f);
    setRenderMs(r);
    setSimMs(s);
    setRasterWidth(raster);
  }, []);

  const toggleSettings = useCallback(() => {
    setSettingsOpen((open) => {
      const next = !open;
      if (next && !window.matchMedia('(min-width: 768px)').matches) setInfoOpen(false);
      return next;
    });
  }, []);

  const toggleInfo = useCallback(() => {
    setInfoOpen((open) => {
      const next = !open;
      if (next && !window.matchMedia('(min-width: 768px)').matches) setSettingsOpen(false);
      return next;
    });
  }, []);

  /* ---- file actions ------------------------------------------------------ */

  const exportPng = useCallback(async () => {
    const state = await runtime.readState();
    const map = stateToHeightmap(state, runtime.backend.width, runtime.getMaxHeight());
    const blob = await heightmapToPngBlob(map);
    download(`mountain-${runtime.params.terrain.seed}-${runtime.backend.width}.png`, blob);
    flash('已导出 16 位 PNG 高程图（高程存放在 R、G 通道）。');
  }, [runtime, flash]);

  const exportBinary = useCallback(async () => {
    const state = await runtime.readState();
    const blob = encodeWorld(state, runtime.snapshotParams(), runtime.backend.width);
    download(`mountain-${runtime.params.terrain.seed}.f32`, blob);
    flash('已导出完整世界状态（.f32：文件头 + 打包状态）。');
  }, [runtime, flash]);

  const saveWorld = useCallback(async () => {
    const state = await runtime.readState();
    const json = {
      format: 'mountain-lab/world-json',
      version: 1,
      width: runtime.backend.width,
      stride: state.length / (runtime.backend.width * runtime.backend.width),
      params: runtime.snapshotParams(),
      // Base64 keeps the payload inside the JSON without doubling the size the
      // way an array of numbers would.
      stateBase64: bytesToBase64(new Uint8Array(state.buffer.slice(0))),
    };
    download(
      `mountain-${runtime.params.terrain.seed}.json`,
      new Blob([JSON.stringify(json)], { type: 'application/json' }),
    );
    flash('已保存世界（状态 + 参数）为 JSON。');
  }, [runtime, flash]);

  const importPng = useCallback(async () => {
    const file = await pickFile('image/png,image/*');
    if (!file) return;
    try {
      const map = await pngBlobToHeightmap(file);
      const state = heightmapToState(map, runtime.getMaxHeight(), runtime.backend.width);
      runtime.writeState(state);
      flash(`Imported ${map.width}×${map.height} heightmap, resampled to ${runtime.backend.width}².`);
    } catch (error) {
      flash(`Import failed: ${(error as Error).message}`);
    }
  }, [runtime, flash]);

  const loadWorld = useCallback(async () => {
    const file = await pickFile('.f32,.json,application/octet-stream,application/json');
    if (!file) return;
    try {
      if (file.name.endsWith('.json')) {
        const parsed = JSON.parse(await file.text()) as {
          width: number;
          params: Parameters<typeof runtime.restoreParams>[0];
          stateBase64: string;
        };
        runtime.restoreParams(parsed.params);
        runtime.writeState(new Float32Array(base64ToBytes(parsed.stateBase64).buffer));
        flash(`Loaded world from ${file.name}.`);
      } else {
        const world = await decodeWorld(file);
        runtime.restoreParams(world.params);
        runtime.writeState(world.state);
        flash(`Loaded world from ${file.name}.`);
      }
    } catch (error) {
      flash(`Load failed: ${(error as Error).message}`);
    }
  }, [runtime, flash]);

  /* ---- keyboard shortcuts ------------------------------------------------ */

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'SELECT') return;
      if (e.code === 'Space') {
        e.preventDefault();
        runtime.toggleRunning();
      } else if (e.key === 's' || e.key === 'S') {
        runtime.step();
      } else if (e.key === 'r' || e.key === 'R') {
        runtime.resetSimulation();
      } else if (e.key === 'b' || e.key === 'B') {
        setBrushActive((v) => !v);
      } else if (e.key === '[' || e.key === ']') {
        const idx = VIEW_MODES.findIndex((m) => m.id === runtime.mode);
        const next = (idx + (e.key === ']' ? 1 : VIEW_MODES.length - 1)) % VIEW_MODES.length;
        runtime.mode = VIEW_MODES[next].id;
        runtime.touch();
      }
    },
    [runtime],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div className="flex h-full flex-col" tabIndex={0} onKeyDown={onKeyDown}>
      <Toolbar
        runtime={runtime}
        brushActive={brushActive}
        onToggleBrush={() => setBrushActive((v) => !v)}
        onExportPng={exportPng}
        onImportPng={importPng}
        onExportBinary={exportBinary}
        onSaveWorld={saveWorld}
        collapsed={!toolbarOpen}
        onToggleCollapsed={() => setToolbarOpen((v) => !v)}
      />

      <div className="relative flex min-h-0 flex-1">
        {settingsOpen || infoOpen ? (
          <button
            type="button"
            aria-label="关闭侧边面板"
            className="absolute inset-0 z-20 bg-ink/10 md:hidden"
            onClick={() => {
              setSettingsOpen(false);
              setInfoOpen(false);
            }}
          />
        ) : null}

        <aside
          className={`${settingsOpen ? 'flex' : 'hidden'} absolute inset-y-0 left-0 z-30 w-[min(86vw,268px)] flex-none overflow-hidden border-r border-rule bg-paper-raised md:relative md:z-auto md:w-[268px]`}
        >
          <button
            type="button"
            aria-label="折叠参数面板"
            aria-expanded={settingsOpen}
            onClick={toggleSettings}
            className="absolute right-1 top-1 z-10 inline-flex h-7 w-7 items-center justify-center border border-rule bg-paper-raised text-ink-muted hover:text-ink"
          >
            <X size={13} />
          </button>
          <SettingsPanel runtime={runtime} />
        </aside>

        <main className="relative min-w-0 flex-1 bg-paper-sunken">
          {!settingsOpen ? (
            <button
              type="button"
              aria-label="展开参数面板"
              aria-expanded={false}
              onClick={toggleSettings}
              className="absolute left-2 top-2 z-10 inline-flex h-8 items-center gap-1 border border-rule bg-paper-raised/95 px-2 text-[11px] text-ink"
            >
              <SlidersHorizontal size={13} />
              参数
            </button>
          ) : null}
          {!infoOpen ? (
            <button
              type="button"
              aria-label="展开信息面板"
              aria-expanded={false}
              onClick={toggleInfo}
              className="absolute right-2 top-2 z-10 inline-flex h-8 items-center gap-1 border border-rule bg-paper-raised/95 px-2 text-[11px] text-ink"
            >
              <Activity size={13} />
              信息
            </button>
          ) : null}
          <Viewport runtime={runtime} onFrameStats={onFrameStats} brushActive={brushActive} />
          {notice ? (
            <div className="pointer-events-none absolute bottom-3 left-1/2 max-w-[70%] -translate-x-1/2 border border-rule bg-paper-raised/95 px-3 py-1.5 text-[11px] text-ink">
              {notice}
            </div>
          ) : null}
          <div className="pointer-events-none absolute left-3 top-12 font-mono text-[10px] leading-relaxed text-ink-faint md:top-3">
            <div>{VIEW_MODES.find((m) => m.id === runtime.mode)?.label}</div>
            {brushActive ? <div className="text-ink-muted">笔刷已启用 — 拖动以修改地形</div> : null}
          </div>
        </main>

        <aside
          className={`${infoOpen ? 'flex' : 'hidden'} absolute inset-y-0 right-0 z-30 w-[min(86vw,212px)] flex-none overflow-hidden border-l border-rule bg-paper-raised md:relative md:z-auto md:w-[212px]`}
        >
          <button
            type="button"
            aria-label="折叠信息面板"
            aria-expanded={infoOpen}
            onClick={toggleInfo}
            className="absolute right-1 top-1 z-10 inline-flex h-7 w-7 items-center justify-center border border-rule bg-paper-raised text-ink-muted hover:text-ink"
          >
            <X size={13} />
          </button>
          <InfoPanel
            runtime={runtime}
            fps={fps}
            renderMs={renderMs}
            simMs={simMs}
            rasterWidth={rasterWidth}
          />
        </aside>
      </div>

      <footer className="hidden flex-none items-center gap-3 border-t border-rule bg-paper-raised px-3 py-1 text-[10px] text-ink-faint md:flex">
        <span>
          <kbd className="font-mono">空格</kbd> 运行/暂停
        </span>
        <span>
          <kbd className="font-mono">S</kbd> 步进
        </span>
        <span>
          <kbd className="font-mono">R</kbd> 重置
        </span>
        <span>
          <kbd className="font-mono">B</kbd> 笔刷
        </span>
        <span>
          <kbd className="font-mono">[ ]</kbd> 切换显示模式
        </span>
        <span className="ml-auto">
          左键拖动旋转 · 右键拖动平移 · 滚轮缩放 ·{' '}
          <button
            type="button"
            className="underline decoration-dotted underline-offset-2 hover:text-ink"
            onClick={loadWorld}
          >
            载入世界
          </button>
        </span>
      </footer>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Base64 helpers for the JSON world format                                   */
/* -------------------------------------------------------------------------- */

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(text: string): Uint8Array {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

void getRuntime;
